/**
 * Virtual Tour Code Mapping
 * Maps project IDs to their corresponding 4-letter virtual tour codes
 * Dynamically fetched from PROD.appcode table via API
 */

import axios from 'axios';
import { API_BASE_URL } from '../API/api';

// Cache for virtual tour mapping to avoid repeated API calls
let virtualTourMappingCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Cache for all virtual tours
let allVirtualToursCache = null;
let allVirtualToursCacheTimestamp = null;

// LocalStorage keys
const VTOURS_CACHE_KEY = 'virtualTours_cache';
const VTOURS_CACHE_TIMESTAMP_KEY = 'virtualTours_cache_timestamp';

/**
 * Fetch virtual tour codes from the API
 * @returns {Promise<Object>} The virtual tour mapping object
 */
const fetchVirtualTourMapping = async () => {
  try {
    console.log('🔗 Fetching virtual tour codes from API...');
    const response = await axios.get(`${API_BASE_URL}/api/virtual-tour-codes`);
    
    if (response.data && typeof response.data === 'object') {
      console.log(`✅ Successfully fetched ${Object.keys(response.data).length} virtual tour codes`);
      return response.data;
    } else {
      console.warn('⚠️ Invalid response format from virtual tour codes API');
      return {};
    }
  } catch (error) {
    console.error('❌ Error fetching virtual tour codes:', error);
    return {};
  }
};

/**
 * Get virtual tour mapping with caching
 * @returns {Promise<Object>} The virtual tour mapping object
 */
const getVirtualTourMapping = async () => {
  const now = Date.now();
  
  // Return cached data if it's still valid
  if (virtualTourMappingCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
    console.log('📦 Using cached virtual tour mapping');
    return virtualTourMappingCache;
  }
  
  // Fetch fresh data
  console.log('🔄 Cache expired or missing, fetching fresh virtual tour mapping');
  virtualTourMappingCache = await fetchVirtualTourMapping();
  cacheTimestamp = now;
  
  return virtualTourMappingCache;
};

/**
 * Preload virtual tour mapping data
 * Call this function to prefetch the mapping data before it's needed
 * @returns {Promise<Object>} The virtual tour mapping object
 */
export const preloadVirtualTourMapping = async () => {
  console.log('🚀 Preloading virtual tour mapping...');
  return await getVirtualTourMapping();
};

/**
 * Get virtual tour code for a project ID
 * @param {number|string} projectId - The project ID
 * @returns {Promise<string|null>} The virtual tour code or null if not found
 */
export const getVirtualTourCode = async (projectId) => {
  try {
    const id = Number(projectId);
    const mapping = await getVirtualTourMapping();
    return mapping[id] || null;
  } catch (error) {
    console.error('❌ Error getting virtual tour code:', error);
    return null;
  }
};

/**
 * Get virtual tour URL for a project
 * @param {number|string} projectId - The project ID
 * @returns {Promise<string>} The virtual tour URL
 */
export const getVirtualTourUrl = async (projectId) => {
  const code = await getVirtualTourCode(projectId);
  if (code) {
    return `http://vtour.123bim.com/${code}/`;
  }
  // Fallback to project ID if no code found
  return `http://vtour.123bim.com/${projectId}/`;
};

/**
 * Fetch all virtual tours from the API
 * @returns {Promise<Object>} Object with project IDs as keys and arrays of tours as values
 */
const fetchAllVirtualTours = async () => {
  try {
    const url = `${API_BASE_URL}/api/virtual-tours`;
    console.log('🔗 Fetching all virtual tours from API...');
    console.log('🔗 API URL:', url);
    
    const response = await axios.get(url);
    
    console.log('🔗 API Response status:', response.status);
    console.log('🔗 API Response data type:', typeof response.data);
    console.log('🔗 API Response data:', response.data);
    
    if (response.data && typeof response.data === 'object') {
      const projectCount = Object.keys(response.data).length;
      const totalTours = Object.values(response.data).reduce((sum, tours) => sum + tours.length, 0);
      console.log(`✅ Successfully fetched ${totalTours} virtual tours across ${projectCount} projects`);
      console.log('✅ Sample project IDs:', Object.keys(response.data).slice(0, 5));
      return response.data;
    } else {
      console.warn('⚠️ Invalid response format from virtual tours API');
      console.warn('⚠️ Response data:', response.data);
      return {};
    }
  } catch (error) {
    console.error('❌ Error fetching virtual tours:', error);
    console.error('❌ Error details:', error.message);
    console.error('❌ Error response:', error.response?.data);
    return {};
  }
};

/**
 * Get all virtual tours with caching (memory + localStorage)
 * @returns {Promise<Object>} Object with project IDs as keys and arrays of tours as values
 */
const getAllVirtualTours = async () => {
  const now = Date.now();
  
  // Check memory cache first
  if (allVirtualToursCache && allVirtualToursCacheTimestamp && (now - allVirtualToursCacheTimestamp) < CACHE_DURATION) {
    console.log('📦 Using in-memory cached virtual tours data');
    return allVirtualToursCache;
  }
  
  // Check localStorage cache
  try {
    const cachedData = localStorage.getItem(VTOURS_CACHE_KEY);
    const cachedTimestamp = localStorage.getItem(VTOURS_CACHE_TIMESTAMP_KEY);
    
    if (cachedData && cachedTimestamp) {
      const timestamp = parseInt(cachedTimestamp, 10);
      if ((now - timestamp) < CACHE_DURATION) {
        console.log('📦 Using localStorage cached virtual tours data');
        allVirtualToursCache = JSON.parse(cachedData);
        allVirtualToursCacheTimestamp = timestamp;
        return allVirtualToursCache;
      } else {
        console.log('⏰ localStorage cache expired');
      }
    }
  } catch (error) {
    console.warn('⚠️ Error reading from localStorage:', error);
  }
  
  // Fetch fresh data
  console.log('🔄 Cache expired or missing, fetching fresh virtual tours data');
  allVirtualToursCache = await fetchAllVirtualTours();
  allVirtualToursCacheTimestamp = now;
  
  // Save to localStorage
  try {
    localStorage.setItem(VTOURS_CACHE_KEY, JSON.stringify(allVirtualToursCache));
    localStorage.setItem(VTOURS_CACHE_TIMESTAMP_KEY, now.toString());
    console.log('💾 Saved virtual tours to localStorage');
  } catch (error) {
    console.warn('⚠️ Error saving to localStorage:', error);
  }
  
  return allVirtualToursCache;
};

/**
 * Preload all virtual tours data
 * Call this function to prefetch all virtual tours before they're needed
 * @returns {Promise<Object>} Object with project IDs as keys and arrays of tours as values
 */
export const preloadAllVirtualTours = async () => {
  console.log('🚀 Preloading all virtual tours...');
  return await getAllVirtualTours();
};

/**
 * Get all virtual tours for a specific project from PROD.vtour table
 * @param {number|string} projectId - The project ID
 * @returns {Promise<Array>} Array of virtual tour objects with code, vtour_name, and projects_name
 */
export const getProjectVirtualTours = async (projectId) => {
  try {
    const allTours = await getAllVirtualTours();
    console.log(`🔍 getProjectVirtualTours: Looking for project ${projectId} (type: ${typeof projectId})`);
    console.log(`🔍 getProjectVirtualTours: Available project IDs:`, Object.keys(allTours));
    
    // Try both as-is and as string/number to handle type mismatches
    const tours = allTours[projectId] || allTours[String(projectId)] || allTours[Number(projectId)] || [];
    console.log(`✅ Retrieved ${tours.length} virtual tours for project ${projectId} from cache`);
    return tours;
  } catch (error) {
    console.error(`❌ Error getting virtual tours for project ${projectId}:`, error);
    return [];
  }
};

/**
 * Get URL for a specific virtual tour by code
 * @param {string} code - The virtual tour code (4-5 chars)
 * @returns {string} The virtual tour URL
 */
export const getVirtualTourUrlByCode = (code) => {
  return `http://vtour.123bim.com/${code}/`;
};

/**
 * Clear virtual tours cache (both memory and localStorage)
 */
export const clearVirtualToursCache = () => {
  console.log('🗑️ Clearing virtual tours cache...');
  allVirtualToursCache = null;
  allVirtualToursCacheTimestamp = null;
  try {
    localStorage.removeItem(VTOURS_CACHE_KEY);
    localStorage.removeItem(VTOURS_CACHE_TIMESTAMP_KEY);
    console.log('✅ Virtual tours cache cleared');
  } catch (error) {
    console.warn('⚠️ Error clearing localStorage cache:', error);
  }
};

export default {
  getVirtualTourCode,
  getVirtualTourUrl,
  getProjectVirtualTours,
  getVirtualTourUrlByCode,
  preloadVirtualTourMapping,
  preloadAllVirtualTours,
  clearVirtualToursCache
};
